import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

import { SessionProvider } from '@/features/auth/SessionProvider';
import { AnnouncerProvider } from '@/lib/a11y';
import { queryClient } from '@/lib/api/queryClient';

import { router } from './routes/router';

/**
 * Provider composition. Order matters:
 *
 *   AnnouncerProvider outside QueryClientProvider, so any screen — including one
 *   rendering a query error — can announce. Its live regions are mounted once, for
 *   the life of the app, which is the only way assistive tech reliably notices
 *   later changes to them.
 *
 *   SessionProvider inside the query client (it uses the same fetch layer) and
 *   outside the router, so the "am I signed in?" bootstrap runs once for the tab
 *   rather than restarting on every navigation — and so the checkout steps can
 *   read it without a route-level loader.
 *
 *   There is no admin session here any more. The operator console is a desktop
 *   application with its own provider tree; this app serves customers only, and
 *   every page used to carry an admin context none of them ever read.
 *
 *   RouterProvider innermost: routes need the query client, the announcer and
 *   the session.
 */
export function App() {
  return (
    <AnnouncerProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </QueryClientProvider>
    </AnnouncerProvider>
  );
}
